import { Platform } from "~/ui/primitives"
import queryString from "query-string"
import { Message, UserStore } from "../models"

export const compareDayAgo = (timestamp) => {
  const time = timestamp
  const today = new Date().getTime()
  const msPerDay = 60 * 1000 * 60 * 24
  const compareTime = today - time
  return Math.round(compareTime / msPerDay)
}

/**
 * 格式化时间
 * @param date Date 时间
 * @param format 格式化 "yyyy-MM-dd hh:mm:ss www"=format
 * @returns {string} 格式化后字符串
 */

export function formatDate(date: Date | string | number, fmt = "D"): string {
  if (!date) {
    return ""
  }
  // const utcDate = new Date(date)
  const localDate = new Date(date)
  let format = "D"
  switch (fmt) {
    case "Y":
      format = "yyyy"
      break
    case "M":
      format = "yyyy-MM"
      break
    case "D":
      format = "yyyy-MM-dd"
      break
    case "h":
      format = "yyyy-MM-dd hh"
      break
    case "m":
      format = "yyyy-MM-dd hh:mm"
      break
    case "s":
      format = "yyyy-MM-dd hh:mm:ss"
      break
    default:
      format = fmt
      break
  }
  const o = {
    "M+": localDate.getMonth() + 1,
    "d+": localDate.getDate(),
    "h+": localDate.getHours(),
    "m+": localDate.getMinutes(),
    "s+": localDate.getSeconds(),
    "q+": Math.floor((localDate.getMonth() + 3) / 3),
    S: localDate.getMilliseconds(),
  }
  const w = [
    ["日", "一", "二", "三", "四", "五", "六"],
    ["周日", "周一", "周二", "周三", "周四", "周五", "周六"],
    ["星期日", "星期一", "星期二", "星期三", "星期四", "星期五", "星期六"],
  ]
  if (/(y+)/.test(format)) {
    format = format.replace(
      RegExp.$1,
      (localDate.getFullYear() + "").substring(4 - RegExp.$1.length),
    )
  }
  if (/(w+)/.test(format)) {
    format = format.replace(RegExp.$1, w[RegExp.$1.length - 1][localDate.getDay()])
  }
  for (const k in o) {
    if (new RegExp("(" + k + ")").test(format)) {
      format = format.replace(
        RegExp.$1,
        RegExp.$1.length === 1 ? o[k] : ("00" + o[k]).substring(("" + o[k]).length),
      )
    }
  }
  return format
}

// 是否是当日时间
const isToday = (date: Date) => {
  const today = new Date()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

// 是否是昨日时间
const isYesterday = (date: Date) => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)

  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  )
}

const isThisYear = (date: Date) => {
  const today = new Date()
  return date.getFullYear() === today.getFullYear()
}
// 格式化消息时间, 更友好的阅读体验
// 当日时间，则显示 小时:分钟
// 昨日时间，则显示 昨天 小时:分钟
// 历史数据，则显示 月-日期 小时:分钟
export const formatMessageTime = (time: Date | string | number) => {
  if (!time) return ""
  let v: Date | string | number = time
  // Support numeric-string timestamps and second-based timestamps.
  if (typeof v === "string" && /^\d+$/.test(v.trim())) v = Number(v)
  if (typeof v === "number" && v > 0 && v < 1e12) v = v * 1000

  const utcDate = new Date(v)
  if (Number.isNaN(utcDate.getTime())) return ""
  const dd = String(utcDate.getDate()).padStart(2, "0")
  const mm = String(utcDate.getMonth() + 1).padStart(2, "0")
  const yyyy = utcDate.getFullYear()

  const HH = String(utcDate.getHours()).padStart(2, "0")
  const MM = String(utcDate.getMinutes()).padStart(2, "0")
  const t = `${HH}:${MM}`

  if (isToday(utcDate)) {
    return t
  } else if (isYesterday(utcDate)) {
    return `昨天 ${t}`
  } else if (isThisYear(utcDate)) {
    return `${mm}-${dd} ${t}`
  } else {
    return `${yyyy}-${mm}-${dd} ${t}`
  }
}

export const formatOrderTime = (time: Date) => {
  if (!time) return false
  const utcDate = new Date(time)
  const localDate =
    Platform.OS === "ios"
      ? new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000)
      : new Date(utcDate.getTime())
  const dd = String(localDate.getDate()).padStart(2, "0")
  const mm = String(localDate.getMonth() + 1).padStart(2, "0")
  const yyyy = localDate.getFullYear()

  const HH = String(localDate.getHours()).padStart(2, "0")
  const MM = String(localDate.getMinutes()).padStart(2, "0")
  const t = `${HH}:${MM}`
  return `${yyyy}-${mm}-${dd} ${t}`
}

export const formatMessageOnlyTime = (time: Date) => {
  if (!time) return false
  const utcDate = new Date(time)
  const HH = String(utcDate.getHours()).padStart(2, "0")
  const MM = String(utcDate.getMinutes()).padStart(2, "0")
  return `${HH}:${MM}`
}

export const hidePhoneNumber = (phone: string) => {
  if (!phone) return ""
  return phone
    .split("")
    .map((n, index) => {
      if ([3, 4, 5].includes(index)) return "*"
      return n
    })
    .join("")
}

export const hideEmail = (email: string) => {
  if (!email) return ""
  const s = String(email).trim()
  const at = s.indexOf("@")
  if (at <= 0) return s
  const name = s.slice(0, at)
  const domain = s.slice(at)
  if (name.length <= 1) return `*${domain}`
  if (name.length === 2) return `${name[0]}*${domain}`
  return `${name.slice(0, 2)}***${name.slice(-1)}${domain}`
}

export const hideContact = (v: string) => {
  if (!v) return ""
  const s = String(v).trim()
  return s.includes("@") ? hideEmail(s) : hidePhoneNumber(s)
}

export const formatMessageOnlyMinsSecs = (time: Date) => {
  if (!time) return ""

  const MM = String(time.getMinutes()).padStart(2, "0")
  const SS = String(time.getSeconds()).padStart(2, "0")
  return `${MM}:${SS}`
}

export const formatMentionId = (
  content: Message["content"],
  mention_ids: Message["mention_ids"],
  userStore: UserStore,
) => {
  if (!content) return ""
  const handleGetDisplayName = (match) => {
    const username = match.replace("@", "")
    const user = userStore.getUserByUsername(username)
    if (user) {
      const displayName = userStore.getDisplayNameByUser(user)
      return displayName ? `@${displayName}` : match
    } else {
      return match
    }
  }
  return content.replace(/@\w+/g, handleGetDisplayName)
}

export const formatBytes = (bytes, decimals = 2, isBinary = false) => {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"] // or ['B', 'KB', 'MB', 'GB', 'TB']

  if (!+bytes) {
    return `0 ${sizes[0]}`
  }

  const inByte = isBinary ? 1024 : 1000
  const dm = decimals < 0 ? 0 : decimals

  const pow = Math.floor(Math.log(bytes) / Math.log(inByte))
  const maxPow = Math.min(pow, sizes.length - 1)

  return `${parseFloat((bytes / Math.pow(inByte, maxPow)).toFixed(dm))} ${sizes[maxPow]}`
}

export const formatPrice = (price) => {
  const arr = String(floorToTwo(Number(price))).split(".")
  let str = arr[0]
    .split("")
    .reverse()
    .reduce((prev, next, index) => {
      return (index % 3 ? next : next + ",") + prev
    })
  if (arr.length === 2) {
    str += `.${arr[1]}`
  }
  return str
}

export const floorToTwo = (v) => Math.floor((v + Number.EPSILON) * 100) / 100

/**
 * 截取小数点后N位
 * @param value
 * @param digits
 * @returns
 */
export const truncateDecimal = (
  value: number | string,
  digits: number = 2
): string => {
  // 1. 统一转为字符串，处理非数字情况
  const s = String(value);
  if (!s || isNaN(Number(s))) return (0).toFixed(digits);

  // 2. 正则匹配：匹配整数部分和小数部分
  // 核心逻辑：找小数点，然后截取小数点后 digits 位
  const regexp = new RegExp(`^-?\\d+(?:\\.\\d{0,${digits}})?`);
  const match = s.match(regexp);
  
  if (!match) return (0).toFixed(digits);

  // 3. 补齐位数（比如输入 10000，digits 为 2，需要输出 10000.00）
  let result = match[0];
  const dotIndex = result.indexOf('.');
  
  if (dotIndex === -1) {
    result += digits > 0 ? '.' + '0'.repeat(digits) : '';
  } else {
    const currentDigits = result.length - dotIndex - 1;
    if (currentDigits < digits) {
      result += '0'.repeat(digits - currentDigits);
    }
  }

  return result;
};


export const formatDateStr = (start, end) => {
  return `${new Date(start).toISOString()} ~ ${new Date(end).toISOString()}`
}

export const normalAdaptor = (obj) => {
  return Object.keys(obj).reduce((acc, key) => {
    if (!["", null, undefined].includes(obj[key])) {
      acc[key] = obj[key]
      if (/_dr$/gi.test(key)) {
        acc[key] = formatDateStr(acc[key][0], acc[key][1])
      }
    }
    return acc
  }, {})
}

export const makeFilters = (filters) => {
  const adaptedFilters = normalAdaptor(filters)
  return decodeURIComponent(
    queryString.stringify({
      filters: JSON.stringify(adaptedFilters),
    }),
  )
}

/**
 * 适配语音时间格式
 * @param durationStr
 */
export const formatWechatVoiceTime = (durationStr: string) => {
  // 输入 00:06、01:10 这种
  const [mm, ss] = durationStr.split(":").map(Number)
  const totalSec = mm * 60 + ss

  if (totalSec < 60) {
    return `${totalSec}"`
  }

  const minutes = Math.floor(totalSec / 60)
  const seconds = totalSec % 60
  return `${minutes}'${seconds}"`
}
